import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BootstrapTable from 'react-bootstrap-table-next';
import { fetchRdsEvents, fetchVacuumStatus } from 'store/admin/rds-monitor/actions';
import { MetaTags } from 'react-meta-tags';
import { AppConfiguration } from 'config';
import { Card, CardBody, Col, Container, Form, FormGroup, Label, Row, TabContent, TabPane } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { customSortCaret, rowStyle } from 'helpers/bootstrap_table_helper';
import { forColumnHeaderWrapper, getIANATimezoneStringForAbbreviation, percentFormatWithTwoDecimalPlaces, sortStringAsFloat } from 'helpers/sf_utils';
import LoadingDashboardTableSkeleton from 'pages/Dashboard/LoadingDashboardTableSkeleton';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import SfNavLink from 'components/Common/SfNavLink';
import { dateCreatedFormatter, onHoverPopUpTooltipFormatter } from '../Users/UserComponents/UserList/Formatters';
import { REPORTING_TIMEZONE, REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants';
import CustomDateRangePicker, { createOption, RDS_DATE_RANGE_OPTIONS_WITH_TIME } from 'components/Common/DateRangePicker';
import Select from 'react-select';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { useRemoteTableSort } from 'hooks/useRemoteTableSort';
import OffsetPagination from 'components/Common/OffsetPagination';

const RDS_EVENTS_PAGE_SIZE = 50;
const DEFAULT_RDS_EVENTS_SORT = { sort_column: 'date', sort_direction: 'desc' };
const RDS_EVENTS_SEARCH_DEBOUNCE_MS = 500;

const RdsMonitor = () => {
    const dispatch = useDispatch();
    const {
        vacuumStatus,
        loading,
        error,
        rdsEvents,
        rdsEventsLoading,
        rdsEventsLastPageFetched,
        rdsEventsHasNextPage,
        rdsEventsTotalNumberOfPages,
        rdsEventsHasLookaheadPages,
        rdsEventsLookaheadPages,
        current_user
    } = useSelector(state => ({
        vacuumStatus: state.RdsMonitorReducer.vacuumStatus,
        loading: state.RdsMonitorReducer.loading,
        error: state.RdsMonitorReducer.error,
        rdsEvents: state.RdsMonitorReducer.rdsEvents,
        rdsEventsLoading: state.RdsMonitorReducer.rdsEventsLoading,
        rdsEventsLastPageFetched: state.RdsMonitorReducer.rdsEventsLastPageFetched,
        rdsEventsHasNextPage: state.RdsMonitorReducer.rdsEventsHasNextPage,
        rdsEventsTotalNumberOfPages: state.RdsMonitorReducer.rdsEventsTotalNumberOfPages,
        rdsEventsHasLookaheadPages: state.RdsMonitorReducer.rdsEventsHasLookaheadPages,
        rdsEventsLookaheadPages: state.RdsMonitorReducer.rdsEventsLookaheadPages,
        current_user: state.LoginAdminUser.current_user
    }));
    const { SearchBar } = Search;
    const [isFeatureFlagActive] = useFeatureFlags();
    const rds_events_tab_ff = useMemo(() => isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_RDS_EVENTS_TAB), [isFeatureFlagActive])

    const rds_event_categories_options = [
        { value: 'all', label: 'All' },
        { value: 'availability', label: 'Availability' },
        { value: 'deletion', label: 'Deletion' },
        { value: 'failover', label: 'Failover' },
        { value: 'failure', label: 'Failure' },
        { value: 'maintenance', label: 'Maintenance' },
        { value: 'notification', label: 'Notification' },
        { value: 'recovery', label: 'Recovery' },
        { value: 'configuration change', label: 'Configuration Change' }
    ]

    const [selectedEventCategory, setSelectedEventCategory] = useState(rds_event_categories_options[0]);

    const [activeTab, setActiveTab] = useState('1');

    const [selectedDateRange, setSelectedDateRange] = useState(
        createOption(
            RDS_DATE_RANGE_OPTIONS_WITH_TIME.LAST_HOUR,
            new Date(),
            getIANATimezoneStringForAbbreviation(
                current_user?.timezone || REPORTING_TIMEZONE
            )
        )
    )

    const [rdsEventsSearchInput, setRdsEventsSearchInput] = useState('');
    const [appliedRdsEventsSearch, setAppliedRdsEventsSearch] = useState('');
    // Kept in a ref so a page/filter/search fetch can reuse the active sort
    // without the request builder having to depend on the sort hook.
    const rdsEventsSortRef = useRef(DEFAULT_RDS_EVENTS_SORT);

    // RDS events are filtered, searched, sorted and paged on the server, so
    // every one of those interactions goes back through this single request.
    const requestRdsEvents = useCallback((overrides = {}) => {
        const dateRange = overrides.dateRange ?? selectedDateRange;
        const category = overrides.category ?? selectedEventCategory;
        const search = overrides.search ?? appliedRdsEventsSearch;
        const sort = overrides.sort ?? rdsEventsSortRef.current;

        dispatch(fetchRdsEvents({
            start_time: dateRange?.value?.startDate ? dateRange.value.startDate.toISOString() : null,
            end_time: dateRange?.value?.endDate ? dateRange.value.endDate.toISOString() : null,
            event_category: category?.value || 'all',
            search: search || '',
            page_number: Math.max(1, Number(overrides.pageNumber) || 1),
            page_size: RDS_EVENTS_PAGE_SIZE,
            sort_column: sort.sort_column,
            sort_direction: sort.sort_direction
        }));
    }, [dispatch, selectedDateRange, selectedEventCategory, appliedRdsEventsSearch]);

    const fetchRdsEventsPage = useCallback((pageNumber) => {
        requestRdsEvents({ pageNumber });
    }, [requestRdsEvents]);

    const onRdsEventsSortChange = useCallback((sort_column, sort_direction) => {
        rdsEventsSortRef.current = { sort_column, sort_direction };
        requestRdsEvents({ pageNumber: 1, sort: rdsEventsSortRef.current });
    }, [requestRdsEvents]);

    const rdsEventsSort = useRemoteTableSort(
        DEFAULT_RDS_EVENTS_SORT.sort_column,
        DEFAULT_RDS_EVENTS_SORT.sort_direction,
        onRdsEventsSortChange
    );

    useEffect(() => {
        dispatch(fetchVacuumStatus());
        requestRdsEvents({ pageNumber: 1 });
    }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce typing before it turns into a request.
    useEffect(() => {
        const timer = setTimeout(
            () => setAppliedRdsEventsSearch(rdsEventsSearchInput.trim()),
            RDS_EVENTS_SEARCH_DEBOUNCE_MS
        );
        return () => clearTimeout(timer);
    }, [rdsEventsSearchInput]);

    const isInitialSearchRender = useRef(true);
    useEffect(() => {
        if (isInitialSearchRender.current) {
            isInitialSearchRender.current = false;
            return;
        }
        requestRdsEvents({ pageNumber: 1 });
    }, [appliedRdsEventsSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    const formatter = (cell) => <span className='font-weight-semibold'>{cell}</span>;

    const columns = [
        {
            dataField: 'table_name',
            text: 'Table Name',
            headerFormatter: forColumnHeaderWrapper,
            formatter: formatter,
            sort: true
        },
        {
            dataField: 'dead_tuples',
            text: 'Dead Tuples',
            headerFormatter: forColumnHeaderWrapper,
            formatter: (cell) => cell?.toLocaleString(),
            sort: true
        },
        {
            dataField: 'vacuum_limit',
            text: 'Vacuum Limit',
            headerFormatter: forColumnHeaderWrapper,
            formatter: (cell) => cell?.toLocaleString(),
            sort: true
        },
        {
            dataField: 'dead_tuples_percentage',
            text: 'Dead Tuples Threshold',
            headerFormatter: forColumnHeaderWrapper,
            formatter: (cell) => formatter(percentFormatWithTwoDecimalPlaces(cell)),
            sort: true,
            sortFunc: sortStringAsFloat
        },
        {
            dataField: 'inserts_since_vacuum',
            text: 'Inserts Since Vacuum',
            headerFormatter: forColumnHeaderWrapper,
            formatter: (cell) => cell?.toLocaleString(),
            sort: true
        },
        {
            dataField: 'vacuum_insert_limit',
            text: 'Vacuum Insert Limit',
            headerFormatter: forColumnHeaderWrapper,
            formatter: (cell) => cell?.toLocaleString(),
            sort: true
        },
        {
            dataField: 'inserts_since_vacuum_percentage',
            text: 'New Insertions Threshold',
            headerFormatter: forColumnHeaderWrapper,
            formatter: (cell) => formatter(percentFormatWithTwoDecimalPlaces(cell)),
            sort: true,
            sortFunc: sortStringAsFloat
        }
    ];

    const defaultSorted = [{
        dataField: 'dead_tuples_percentage',
        order: 'desc'
    }];

    const isThresholdCrossed = (row) => {
        return (row.dead_tuples >= 10_000 && row.dead_tuples_percentage >= 100) ||
            (row.inserts_since_vacuum >= 10_000 && row.inserts_since_vacuum_percentage >= 100);
    }

    const applyFilters = (selected_date_range) => {
        setSelectedDateRange(selected_date_range);
        setSelectedEventCategory(rds_event_categories_options[0]);
        requestRdsEvents({
            pageNumber: 1,
            dateRange: selected_date_range,
            category: rds_event_categories_options[0]
        });
    }

    const handleEventCategoryChange = (selected_event_category) => {
        setSelectedEventCategory(selected_event_category);
        requestRdsEvents({ pageNumber: 1, category: selected_event_category });
    };

    const rdsEventsColumns = [
        {
            dataField: 'date',
            text: 'Timestamp',
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            formatter: dateCreatedFormatter
        },
        {
            sort: true,
            headerFormatter: forColumnHeaderWrapper,
            dataField: 'source_identifier',
            text: 'Source Identifier',
        },
        {
            sort: true,
            headerFormatter: forColumnHeaderWrapper,
            dataField: 'message',
            text: 'Message',
            style: { maxWidth: '250px', textOverflow: 'ellipsis', overflow: 'hidden' },
            formatter: ((cellContent, row, rowIndex) => onHoverPopUpTooltipFormatter(cellContent, row, rowIndex, "lead_refund_reason"))
        },
        {
            sort: true,
            headerFormatter: forColumnHeaderWrapper,
            dataField: 'event_categories',
            text: 'Event Categories',
        }
    ];

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title>RDS Monitor | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="" breadcrumbItem="RDS Monitor" />
                    <Card>
                        <CardBody>
                            {rds_events_tab_ff && (
                                <div className="d-flex justify-content-between">
                                    <ul className="nav sfnav mb-4">
                                        <li className="nav-item">
                                            <SfNavLink position="1" tabName="Vacuum Monitor" customActiveTab={activeTab} setcustomActiveTab={setActiveTab} />
                                        </li>
                                        <li className="nav-item">
                                            <SfNavLink position="2" tabName="RDS Events" customActiveTab={activeTab} setcustomActiveTab={setActiveTab} />
                                        </li>
                                    </ul>
                                </div>)}
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <Row>
                                        <Col xl="12">
                                            <ToolkitProvider
                                                keyField="id"
                                                columns={columns}
                                                data={vacuumStatus}
                                                search
                                            >
                                                {(toolkitProps) => (
                                                    <>
                                                        <Form>
                                                            <Row>
                                                                <Col xl="auto" className="align-self-end">
                                                                    <div className="mt-1">
                                                                        <div className="search-box d-inline-block mb-1">
                                                                            <div className="position-relative">
                                                                                <SearchBar
                                                                                    className="rounded"
                                                                                    {...toolkitProps.searchProps}
                                                                                />
                                                                                <i className="bx bx-search-alt search-icon" />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Form>
                                                        <Row className="sfTable-Container" style={{ maxHeight: '60vh' }}>
                                                            <Col xl="12">
                                                                {loading ? (
                                                                    <LoadingDashboardTableSkeleton rows={265} cols={columns.length} />
                                                                ) : (
                                                                    <BootstrapTable
                                                                        keyField="table_name"
                                                                        data={vacuumStatus}
                                                                        columns={columns}
                                                                        responsive
                                                                        bordered={false}
                                                                        striped={false}
                                                                        defaultSorted={defaultSorted}
                                                                        rowClasses={(row) => (isThresholdCrossed(row) ? "tr-hover-deleted" : "tr-hover")}
                                                                        rowStyle={(row) => rowStyle(row, isThresholdCrossed(row))}
                                                                        headerClasses="sftableHeader thead-h50 text-black"
                                                                        classes="table align-middle table-nowrap text-black"
                                                                        headerWrapperClasses="thead-light"
                                                                        noDataIndication={() => "Unable to load the vacuum statuses for the tables"}
                                                                        sort={{ sortCaret: customSortCaret }}
                                                                        {...toolkitProps.baseProps}
                                                                    />
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )}
                                            </ToolkitProvider>
                                            {error && <p className="text-danger mt-3">{error}</p>}
                                        </Col>
                                    </Row>
                                </TabPane>
                                <TabPane tabId="2">
                                    <Row>
                                        <Col xl="12">
                                            <Form onSubmit={(e) => e.preventDefault()}>
                                                <Row>
                                                    <Col xl="auto" className="align-self-end">
                                                        <div className="mt-1">
                                                            <div className="search-box d-inline-block mb-1">
                                                                <div className="position-relative">
                                                                    {/* Searched on the server alongside the paging, so
                                                                        it matches every event and not just this page. */}
                                                                    <input
                                                                        type="search"
                                                                        className="form-control rounded"
                                                                        placeholder="Search"
                                                                        aria-label="Search RDS events"
                                                                        value={rdsEventsSearchInput}
                                                                        onChange={(e) => setRdsEventsSearchInput(e.target.value)}
                                                                    />
                                                                    <i className="bx bx-search-alt search-icon" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md="3">
                                                        <FormGroup className="mb-0">
                                                            <Label className="font-weight-semibold">Time</Label>
                                                            <div>
                                                                <CustomDateRangePicker
                                                                    ianaTz={getIANATimezoneStringForAbbreviation(
                                                                        current_user?.timezone || REPORTING_TIMEZONE
                                                                    )}
                                                                    value={selectedDateRange}
                                                                    onChange={applyFilters}
                                                                    options={Object.keys(RDS_DATE_RANGE_OPTIONS_WITH_TIME).map(opt =>
                                                                        createOption(
                                                                            RDS_DATE_RANGE_OPTIONS_WITH_TIME[opt],
                                                                            new Date(),
                                                                            getIANATimezoneStringForAbbreviation(
                                                                                current_user?.timezone || REPORTING_TIMEZONE
                                                                            )
                                                                        )
                                                                    )}
                                                                />
                                                            </div>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="2">
                                                        <FormGroup className="mb-0">
                                                            <Label className="font-weight-semibold">
                                                                Event Category
                                                            </Label>
                                                            <Select
                                                                name="event_category"
                                                                options={rds_event_categories_options}
                                                                onChange={handleEventCategoryChange}
                                                                value={selectedEventCategory}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            </Form>
                                            <Row className="sfTable-Container" style={{ maxHeight: '60vh' }}>
                                                <Col xl="12">
                                                    {rdsEventsLoading ? (
                                                        <LoadingDashboardTableSkeleton rows={15} cols={rdsEventsColumns.length} />
                                                    ) : (
                                                        <BootstrapTable
                                                            keyField="id"
                                                            data={rdsEvents}
                                                            columns={rdsEventsColumns}
                                                            responsive
                                                            bordered={false}
                                                            striped={false}
                                                            defaultSorted={rdsEventsSort.defaultSorted}
                                                            remote={rdsEventsSort.remote}
                                                            onTableChange={rdsEventsSort.onTableChange}
                                                            rowClasses="tr-hover"
                                                            headerClasses="sftableHeader thead-h50 text-black"
                                                            classes="table align-middle table-nowrap text-black"
                                                            headerWrapperClasses="thead-light"
                                                            noDataIndication={() => "You currently don't have any RDS events"}
                                                            sort={{ sortCaret: customSortCaret }}
                                                        />
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xl="auto" className="ms-auto">
                                                    <OffsetPagination
                                                        currentPage={rdsEventsLastPageFetched}
                                                        totalPages={rdsEventsTotalNumberOfPages}
                                                        hasNextPage={rdsEventsHasNextPage}
                                                        hasDeepOffsetPages={rdsEventsHasLookaheadPages}
                                                        offsetLookAheadPages={rdsEventsLookaheadPages}
                                                        loading={rdsEventsLoading}
                                                        onPageChange={fetchRdsEventsPage}
                                                        className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                                    />
                                                </Col>
                                            </Row>
                                            {error && <p className="text-danger mt-3">{error}</p>}
                                        </Col>
                                    </Row>
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    )

};

export default RdsMonitor;